/**
 * Renders an evidence fact with its `DocChunk#N` references kept inline as
 * clickable {@link DocChunkChip}s (instead of stripped), so a reference can
 * deep-link the left-hand clinical PDF to the chunk's page.
 *
 * The prose is preserved in its original order/spacing around the chips, and the
 * first letter of the first prose segment is capitalized to sentence-case the
 * fact.
 * @module EvidenceText
 */
import React, { Fragment } from "react";
import { ChunkLocation, parseEvidenceText } from "../utils/evidenceText";
import { DocChunkChip } from "./DocChunkChip";

/** Props for {@link EvidenceText}. */
interface EvidenceTextProps {
  /** The raw evidence fact text (may contain `DocChunk#NN` references). */
  text: string;
  /**
   * Chunk-location lookup for this fact's parent item. Absent (or a miss) when a
   * cited chunk has no resolved location, in which case the chip is static.
   */
  locations?: Map<number, ChunkLocation>;
  /** Invoked with the 1-based PDF page when a navigable chip is clicked. */
  onNavigate?: (page: number) => void;
}

/** Uppercases the first letter in the string; a no-op when it has none. */
const capitalizeFirst = (value: string): string => {
  for (let i = 0; i < value.length; i += 1) {
    const char = value[i];
    if (/[a-zA-Z]/.test(char)) {
      return value.slice(0, i) + char.toUpperCase() + value.slice(i + 1);
    }
  }
  return value;
};

/**
 * Renders an evidence fact with inline, navigable chunk references.
 *
 * @param props - Fact text, its chunk-location map, and the navigate handler
 * @returns The fact rendered as prose plus inline {@link DocChunkChip}s
 */
export const EvidenceText: React.FC<EvidenceTextProps> = ({
  text,
  locations,
  onNavigate,
}) => {
  const segments = parseEvidenceText(text);
  let capitalizeNext = true;

  return (
    <>
      {segments.map((segment, index) => {
        if (segment.chunkNumber !== undefined) {
          return (
            <DocChunkChip
              key={index}
              chunkNumber={segment.chunkNumber}
              location={locations?.get(segment.chunkNumber)}
              onNavigate={onNavigate}
            />
          );
        }
        const prose = capitalizeNext
          ? capitalizeFirst(segment.text)
          : segment.text;
        if (segment.text.trim().length > 0) capitalizeNext = false;
        return <Fragment key={index}>{prose}</Fragment>;
      })}
    </>
  );
};
